// PUT com progresso de UPLOAD e cancelamento.
//
// Por que XHR e não fetch: `fetch` não expõe progresso de upload em nenhum
// browser (a proposta de request streams não cobre isso de forma utilizável).
// Sem progresso, um envio de 2 GiB seria uma barra parada por 20 minutos — e
// foi exatamente a falta de sinal que tornou o bug dos 400 MB indiagnosticável.

/** Falha HTTP com status — o chamador decide entre retentar e desistir. */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    message?: string
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
  }
}

/** Rede caiu, DNS falhou, TLS quebrou — sem status, sempre retentável. */
export class NetworkError extends Error {
  constructor(message = 'Falha de rede') {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Cancelamento deliberado. Nunca deve virar retentativa nem toast de erro. */
export class AbortedError extends Error {
  constructor() {
    super('Envio cancelado');
    this.name = 'AbortedError';
  }
}

export interface XhrPutOptions {
  url: string;
  body: Blob;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Bytes enviados até agora DESTE corpo. Pode regredir se houver retentativa. */
  onProgress?: (loaded: number) => void;
}

export function xhrPut({
  url,
  body,
  headers = {},
  signal,
  timeoutMs = 10 * 60_000,
  onProgress,
}: XhrPutOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new AbortedError());
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.timeout = timeoutMs;
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    const cleanup = () => signal?.removeEventListener('abort', onAbort);

    if (onProgress) {
      xhr.upload.onprogress = (e) => onProgress(e.loaded);
    }

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        // O progresso não fecha sozinho em 100%: alguns browsers não emitem o
        // último `progress`. Sem isto a barra encalha em ~99% no bloco final.
        onProgress?.(body.size);
        resolve();
        return;
      }
      reject(new HttpError(xhr.status, xhr.responseText ?? ''));
    };
    // `onerror` não distingue rede de CORS de propósito: o browser não conta
    // qual foi, para não virar canal de sondagem. Ambos são retentáveis.
    xhr.onerror = () => {
      cleanup();
      reject(new NetworkError());
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new NetworkError('Tempo limite do bloco'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new AbortedError());
    };

    xhr.send(body);
  });
}
